import type { GetSurveyResultResponse } from '@genially/contracts';

const adjustArray = (
  array: {
    id: string;
    percentage: number;
    total: number;
  }[],
) => {
  const currentSum = array.reduce((total, item) => total + item.percentage, 0);
  const difference = 100 - currentSum;

  if (currentSum === 0) {
    return array;
  }

  return array.map(item => ({
    ...item,
    percentage: Math.round(item.percentage + (item.percentage / currentSum) * difference),
  }));
};

interface CalculateSurveyPercentagesProps {
  result: GetSurveyResultResponse;
  answerIds: string[];
  localAnswers?: string[];
  answersLocalStorageKey?: string;
}

export const calculateSurveyPercentages = ({
  result,
  answerIds,
  localAnswers,
  answersLocalStorageKey,
}: CalculateSurveyPercentagesProps) => {
  const questionCounts: { [questionId: string]: number } = {};
  let totalQuestions = result.reduce((acc, item) => acc + item.totalAnswers, 0);

  // Inicializar todas las preguntas con un conteo de 0
  answerIds.forEach(answerId => {
    questionCounts[answerId] = 0;
  });

  // Calcular el conteo de respuestas para cada pregunta en el resultado
  // eslint-disable-next-line no-restricted-syntax
  for (const item of result) {
    questionCounts[item.answerId] = item.totalAnswers;
  }

  localAnswers?.forEach(answerId => {
    questionCounts[answerId] += 1;
    totalQuestions += 1;
  });

  if (answersLocalStorageKey) {
    const savedAnswers = localStorage.getItem(answersLocalStorageKey);

    if (savedAnswers) {
      const parsedSavedAnswers = JSON.parse(savedAnswers);
      // remove saved answers if they are older than 30 seconds
      const filteredSavedAnswers = parsedSavedAnswers.filter(
        (item: { timestamp: number }) => item.timestamp > Date.now() - 30 * 1000,
      );

      filteredSavedAnswers.forEach((item: { answersSelected?: string[] }) => {
        item.answersSelected?.forEach(answerId => {
          questionCounts[answerId] += 1;
          totalQuestions += 1;
        });
      });

      localStorage.setItem(answersLocalStorageKey, JSON.stringify(filteredSavedAnswers));
    }
  }

  const percentages: {
    id: string;
    percentage: number;
    total: number;
  }[] = [];

  answerIds.forEach(answerId => {
    const count = questionCounts[answerId] || 0;
    const percentage = (count * 100) / totalQuestions;

    if (!Number.isNaN(percentage)) {
      percentages.push({
        id: answerId,
        percentage: Number(percentage),
        total: count,
      });
    }
  });

  if (percentages.length > 0) {
    return adjustArray(percentages);
  }

  return [];
};
